import React, { useEffect, useState } from "react";
import { Button, Form, Input, Table, Typography } from "antd";
import { ColumnsType } from "antd/es/table";
import {
  CrossIcon,
  DeleteIcon,
  PencilIcon,
  PlusIcon,
  TickIcon,
} from "../../../../../atoms/CustomIcons";
import { useLocation } from "react-router-dom";
import {
  createUnitByCompanyId,
  deleteUnit,
  deleteUnitsById,
} from "../../ducks/services";
import { AxiosError } from "axios";
import SuccessMessage from "../../../../../atoms/Toasts/SuccessMessage";
import ErrorMessage from "../../../../../atoms/Toasts/ErrorMessage";
import { UnitsTableType } from "../../ducks/types";
import { getUnitsById } from "../../ducks/actions";
import { useAppDispatch } from "../../../../../config/hooks";
import ConfirmDelete from "../../../../../atoms/ConfirmDelete";
import { PopUp } from "../../../../../atoms/PopUp";
import { createStyles } from "antd-style";
import { TableRowSelection } from "antd/es/table/interface";

interface DataType {
  id?: string;
  name: string | null;
  singular_name: string | null;
  plural_name: string | null;
}

interface EditableCellProps {
  editing: boolean;
  dataIndex: string;
  title: string;
  inputType: "number" | "text";
  record: DataType;
  index: number;
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode = (
    <Input
      placeholder={dataIndex === "name" ? `Enter unit full name` : title}
      maxLength={dataIndex !== "name" ? 5 : undefined}
      className="editTableInput"
    />
  );

  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: "",
              // message:
              //   dataIndex === "name"
              //     ? "Unit full name is required"
              //     : `${title} is required`,
            },
            {
              validator: (_, value) => {
                if (!value) {
                  // If the field is empty, let the 'required' rule handle it.
                  // Or, if not required, resolve immediately.
                  return Promise.resolve();
                }
                if (typeof value === "string" && value.startsWith(" ")) {
                  return Promise.reject(new Error(""));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

interface PropsType {
  data: UnitsTableType[] | null;
}

export default function EditableTable(props: PropsType) {
  const useStyle = createStyles(({ css }) => {
    return {
      customTable: css`
        .ant-table {
          .ant-table-container {
            .ant-table-body,
            .ant-table-content {
              scrollbar-width: thin;
              scrollbar-color: transparent transparent;
              scrollbar-gutter: stable;
              scrollbar-border-radius: 30px;
              transition: scrollbar-color 0.5s;

              &:hover {
                scrollbar-color: #dee5e6 transparent;
              }
            }
            .ant-table-thead {
              .ant-table-cell {
                height: 40px;
              }
            }
          }
        }
      `,
    };
  });
  const { styles } = useStyle();

  const { data } = props;
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteModalMultiple, setIsDeleteModalMultiple] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<DataType[]>();
  const [editingKey, setEditingKey] = useState<null | string>(null);
  const [deleteId, setDeleteId] = useState<null | string>(null);
  const location = useLocation();
  const pathSegments = location.pathname.split("/");
  const companyId = pathSegments[pathSegments.length - 1];
  const [selectedRowKeys, setSelectedRowKeys] = useState<
    React.Key[] | string[]
  >([]);

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection: TableRowSelection<DataType> = {
    selectedRowKeys,
    onChange: onSelectChange,
    type: "checkbox",
    getCheckboxProps: (record: DataType) => ({
      disabled: record.id === "1",
      name: record.name ?? undefined,
    }),
  };

  useEffect(() => {
    if (data) {
      setDataSource(data);
    }
  }, [data]);

  const isEditing = (record: DataType) => record.id === editingKey;

  const edit = (record: Partial<DataType>) => {
    form.setFieldsValue({
      name: "",
      singular_name: "",
      plural_name: "",
      ...record,
    });
    setEditingKey(record?.id ?? null);
  };

  const cancel = () => {
    if (editingKey !== null) {
      // Check if the row being cancelled is the newly added row (has null values)
      const isNewRow = dataSource?.some((item) => item.id === "1");

      if (isNewRow) {
        // Remove the newly added row
        setDataSource(dataSource?.filter((item) => item.id !== editingKey));
        setEditingKey(null);
      } else {
        // If it's an existing row being edited, just cancel editing
        setEditingKey(null);
      }
    }
  };

  const handleAdd = () => {
    const newData: DataType = {
      id: "1",
      name: null,
      singular_name: null,
      plural_name: null,
    };
    setDataSource([newData, ...(dataSource || [])]);
    edit(newData);
  };

  const save = async () => {
    const item = (await form.validateFields()) as DataType;
    console.log("item", item);

    const newData: DataType[] = [...(dataSource || [])];
    const index = newData.findIndex((item) => editingKey === item.id);
    const payload = {
      id: editingKey === "1" ? undefined : editingKey,
      name: item?.name,
      singular_name: item?.singular_name,
      plural_name: item?.plural_name,
    };

    createUnitByCompanyId(companyId, payload)
      .then(() => {
        SuccessMessage(`Unit list ${editingKey === "1" ? "added" : "updated"}`);
        if (item.id !== "1") {
          const oldItems = newData[index];
          newData.splice(index, 1, {
            ...oldItems,
            ...item,
          });
          setDataSource(newData);
          setEditingKey(null);
        } else {
          newData.push(item);
          setDataSource(newData);
          setEditingKey(null);
        }
        dispatch(getUnitsById(companyId));
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          ErrorMessage("Something went wrong");
        }
      });
  };

  const apiSubmitDeleteUnit = () => {
    setLoading(true);
    deleteUnitsById(deleteId ?? "")
      .then(() => {
        SuccessMessage("Measurement units deleted");
        setIsDeleteModalOpen(false);
        setSelectedRowKeys([]);
        dispatch(getUnitsById(companyId));
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          ErrorMessage("Something went wrong");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const apiSubmitDeleteMultiple = () => {
    setLoading(true);
    deleteUnit(selectedRowKeys)
      .then(() => {
        SuccessMessage("Measurement units deleted");
        setIsDeleteModalMultiple(false);
        setSelectedRowKeys([]);
        dispatch(getUnitsById(companyId));
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          ErrorMessage("Something went wrong");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const ConfirmDeletePopUp = {
    visibility: isDeleteModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsDeleteModalOpen(false)}
        heading="Confirm deletion"
        paragraph={
          <>
            This unit will not be available anymore. <br />
            This action cannot be undone.
          </>
        }
        btnText="Delete"
        btnApi={() => apiSubmitDeleteUnit()}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsDeleteModalOpen(false),
  };

  const ConfirmDeletePopUpMultiple = {
    visibility: isDeleteModalMultiple,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsDeleteModalMultiple(false)}
        heading="Confirm deletion"
        paragraph={
          <>
            Selected {selectedRowKeys?.length === 1 ? "unit" : "units"} will not
            be available anymore.
            <br />
            This action cannot be undone.
          </>
        }
        btnText={`Delete ${selectedRowKeys?.length} ${
          selectedRowKeys?.length === 1 ? "unit" : "units"
        }`}
        btnApi={() => apiSubmitDeleteMultiple()}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsDeleteModalMultiple(false),
  };

  const columns = [
    {
      title: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          Unit full name
          <Button
            className="btn-sm"
            onClick={handleAdd}
            icon={<PlusIcon />}
            disabled={dataSource?.some((item) => item.id === "1")}
          />
        </div>
      ),
      dataIndex: "name",
      width: 466,
      editable: true,
    },
    {
      title: "Short name (singular)",
      dataIndex: "singular_name",
      width: 230,
      editable: true,
    },
    {
      title: "Short name (plural)",
      dataIndex: "plural_name",
      width: 200,
      editable: true,
    },
    {
      title: "",
      width: 104,
      dataIndex: "status",
      render: (_: string, record: DataType) => {
        const editable = isEditing(record);

        return editable ? (
          <span className="actionIcons">
            <Typography.Link
              onClick={() => save()}
              style={{ marginInlineEnd: 8 }}
            >
              <TickIcon style={{ color: "rgb(21, 23, 24)" }} />
            </Typography.Link>
            <Typography.Link onClick={cancel} style={{ marginInlineEnd: 8 }}>
              <CrossIcon style={{ color: "rgb(21, 23, 24)" }} />
            </Typography.Link>
          </span>
        ) : (
          <span className="actionIcons">
            <Typography.Link
              disabled={editingKey !== null}
              onClick={() => edit(record)}
              style={{ marginInlineEnd: 8 }}
            >
              <PencilIcon />
            </Typography.Link>
            <Typography.Link
              disabled={editingKey !== null}
              onClick={() => {
                setDeleteId(record?.id ?? null);
                setIsDeleteModalOpen(true);
              }}
              className="deleteIconHover"
            >
              <DeleteIcon style={{ color: "rgb(21, 23, 24)" }} />
            </Typography.Link>
          </span>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: DataType) => ({
        record,
        inputType: "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  return (
    <Form form={form} component={false}>
      <Table<DataType>
        components={{
          body: { cell: EditableCell },
        }}
        className={styles.customTable}
        rowSelection={rowSelection}
        dataSource={dataSource}
        columns={mergedColumns as ColumnsType<DataType>}
        rowClassName="editable-row"
        pagination={false}
        rowKey="id"
      />
      {selectedRowKeys?.length !== 0 ? (
        <div className="selection_bar">
          <div className="selected_count">
            <Button
              icon={<CrossIcon style={{ color: "white" }} />}
              className="only_icon"
              type="text"
              onClick={() => setSelectedRowKeys([])}
            />
            {selectedRowKeys.length}{" "}
            {`${selectedRowKeys.length === 1 ? "unit" : "units"}`} selected
          </div>
          <div className="selection_buttons">
            <Button
              type="text"
              icon={<DeleteIcon style={{ color: "white" }} />}
              onClick={() => {
                setIsDeleteModalMultiple(true);
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ) : null}
      <PopUp {...ConfirmDeletePopUp} />
      <PopUp {...ConfirmDeletePopUpMultiple} />
    </Form>
  );
}
